// One list item = one block.
//
// A list directly under the doc (bullet, ordered, task) holds exactly one item;
// a list of N items is split into N one-item lists. The markdown and the look
// stay one list; only the blocks (ids, handles, conversations) become items.
// Because block ids live only at depth 0, the catalog, MCP and comments work per
// item without knowing about lists.
//
// Split in two places:
//   1. reading markdown - {@link splitListBlocks} (JSON, called by parseMarkdown)
//   2. editing          - {@link ListBlockPlugin} (append transaction)
//
// Nested items are not blocks: an item indented with Tab becomes a child of the
// previous item and loses its id.

import { type Editor, Extension, type JSONContent } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import { canJoin } from "@tiptap/pm/transform";
import { isYSyncTransaction } from "./block-id-plugin";

/** List types split into one-item lists at the top level. */
export const TOP_LEVEL_LIST_TYPES: readonly string[] = [
  "bulletList",
  "orderedList",
  "taskList",
];

function isListType(name: string | undefined): boolean {
  return name !== undefined && TOP_LEVEL_LIST_TYPES.includes(name);
}

/**
 * `start` of a top-level ordered list is always 1. The number is not kept in
 * the document: inserting one item would otherwise change every following
 * item's `start`, so every following content hash would change. The screen
 * numbers with a CSS counter and the markdown writer numbers by position
 * (render-blocks.ts).
 */
function normalizeStart(attrs: Record<string, unknown> | undefined): {
  attrs: Record<string, unknown>;
  changed: boolean;
} {
  const next = { ...(attrs ?? {}) };
  const changed = "start" in next && next.start !== 1 && next.start != null;
  if (changed) next.start = 1;
  return { attrs: next, changed };
}

/**
 * Splits the top-level lists of a PM JSON doc into one-item lists. Returns the
 * same instance when nothing changes. Lists not at the top level (inside a
 * toggle) are untouched.
 */
export function splitListBlocks(doc: JSONContent): JSONContent {
  const out: JSONContent[] = [];
  let changed = false;
  for (const block of doc.content ?? []) {
    if (!isListType(block.type)) {
      out.push(block);
      continue;
    }
    const items = block.content ?? [];
    const { attrs, changed: startChanged } = normalizeStart(block.attrs);
    if (items.length <= 1) {
      out.push(startChanged ? { ...block, attrs } : block);
      changed ||= startChanged;
      continue;
    }
    for (const item of items) out.push({ ...block, attrs, content: [item] });
    changed = true;
  }
  return changed ? { ...doc, content: out } : doc;
}

/**
 * The transaction splitting top-level lists of two or more items, or undefined
 * (the second round must return undefined). `tr.split` copies attributes, so
 * both halves share an id; the block id plugin's dedupe keeps it on the first.
 */
function splitListBlocksTr(
  tr: Transaction,
  doc: PMNode,
): Transaction | undefined {
  const targets: { pos: number; node: PMNode }[] = [];
  let pos = 0;
  doc.forEach((child) => {
    const childPos = pos;
    pos += child.nodeSize;
    if (!isListType(child.type.name)) return;
    if (child.childCount > 1) targets.push({ pos: childPos, node: child });
    else if (child.attrs?.start != null && child.attrs.start !== 1)
      targets.push({ pos: childPos, node: child });
  });
  if (targets.length === 0) return undefined;

  // From the back, so earlier positions stay valid.
  for (const { pos: listPos, node } of targets.reverse()) {
    if (node.attrs?.start != null && node.attrs.start !== 1) {
      tr.setNodeMarkup(listPos, undefined, { ...node.attrs, start: 1 });
    }
    if (node.childCount <= 1) continue;
    const offsets: number[] = [];
    let itemPos = listPos + 1;
    node.forEach((item) => {
      offsets.push(itemPos);
      itemPos += item.nodeSize;
    });
    for (let i = offsets.length - 1; i >= 1; i--) tr.split(offsets[i], 1);
  }
  return tr;
}

export const listBlockPluginKey = new PluginKey("trameriaListBlock");

export function createListBlockPlugin(): Plugin {
  return new Plugin({
    key: listBlockPluginKey,
    appendTransaction(transactions, _oldState, newState) {
      if (!transactions.some((tr) => tr.docChanged)) return undefined;
      // Not on y-sync transactions: two clients splitting the same old
      // document at once would merge into duplicated items. The server is the
      // source, as for ids.
      if (transactions.some(isYSyncTransaction)) return undefined;
      return splitListBlocksTr(newState.tr, newState.doc);
    },
  });
}

// --- keys --------------------------------------------------------------------
//
// Three default commands assume sibling items inside one list and stop working;
// they are handled first. When the conditions do not hold, false falls through
// to the default.

/** The top-level block (depth 1) at the caret, if it is a list. */
function topLevelListAt(editor: Editor): {
  list: PMNode;
  index: number;
  itemTypeName: string;
} | null {
  const { $from } = editor.state.selection;
  if ($from.depth < 3) return null;
  const list = $from.node(1);
  if (!isListType(list.type.name)) return null;
  return {
    list,
    index: $from.index(0),
    itemTypeName: $from.node(2).type.name,
  };
}

/** The caret is at the very start of its block. */
function atBlockStart(editor: Editor): boolean {
  const { $from, empty } = editor.state.selection;
  if (!empty || $from.parentOffset !== 0) return false;
  for (let d = 1; d < $from.depth; d++) if ($from.index(d) !== 0) return false;
  return true;
}

/** The caret is at the very end of its block. */
function atBlockEnd(editor: Editor): boolean {
  const { $from, empty } = editor.state.selection;
  if (!empty || $from.parentOffset !== $from.parent.content.size) return false;
  for (let d = 1; d < $from.depth; d++)
    if ($from.index(d) !== $from.node(d).childCount - 1) return false;
  return true;
}

/** The previous (-1) or next (+1) top-level sibling is a list of the same type. */
function siblingListSameType(editor: Editor, offset: -1 | 1): boolean {
  const here = topLevelListAt(editor);
  if (here === null) return false;
  const idx = here.index + offset;
  const doc = editor.state.doc;
  if (idx < 0 || idx >= doc.childCount) return false;
  return doc.child(idx).type === here.list.type;
}

/**
 * Tab: join with the previous sibling list, then `sinkListItem`. In a one-item
 * list `sinkListItem` finds no previous item and always fails.
 */
export function indentListBlock(editor: Editor): boolean {
  const here = topLevelListAt(editor);
  if (here === null || here.list.childCount !== 1) return false;
  if (!siblingListSameType(editor, -1)) return false;
  return editor
    .chain()
    .command(({ tr }) => {
      const pos = tr.selection.$from.before(1);
      if (!canJoin(tr.doc, pos)) return false;
      tr.join(pos);
      return true;
    })
    .sinkListItem(here.itemTypeName)
    .run();
}

/** The list's only item is one empty paragraph (just made with Enter). */
function isEmptyListItem(list: PMNode): boolean {
  if (list.childCount !== 1) return false;
  const paragraph =
    list.firstChild?.childCount === 1 ? list.firstChild.firstChild : null;
  return !!paragraph?.isTextblock && paragraph.content.size === 0;
}

/**
 * Backspace at the start of an item. An empty item leaves the list (becomes a
 * paragraph); joining it with the item above would leave Enter as the only way
 * out. An item with text joins the previous list's last textblock as text: the
 * default `joinBackward` would join the lists as structure, which the plugin
 * splits again, so nothing would seem to happen.
 */
export function joinListBlockBackward(editor: Editor): boolean {
  if (!atBlockStart(editor)) return false;
  const here = topLevelListAt(editor);
  if (here === null) return false;
  if (isEmptyListItem(here.list)) {
    return editor.commands.liftListItem(here.itemTypeName);
  }
  if (!siblingListSameType(editor, -1)) return false;
  return editor.commands.joinTextblockBackward();
}

/** Delete at the end of an item: join the next list's first textblock. */
export function joinListBlockForward(editor: Editor): boolean {
  if (!atBlockEnd(editor)) return false;
  if (!siblingListSameType(editor, 1)) return false;
  return editor.commands.joinTextblockForward();
}

/**
 * The split plugin and the keys, for live editing only (parsing splits through
 * {@link splitListBlocks}). High priority so the keys run before ListItem's.
 */
export const ListBlockPlugin = Extension.create({
  name: "listBlockPlugin",
  priority: 1000,

  addProseMirrorPlugins() {
    return [createListBlockPlugin()];
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => indentListBlock(this.editor),
      Backspace: () => joinListBlockBackward(this.editor),
      Delete: () => joinListBlockForward(this.editor),
    };
  },
});
